/**
 * AnalyzeTagsButton Component (Phase 22: FR-095, FR-109)
 * Button to trigger manual AI tag analysis for a location.
 */

import * as React from 'react';
import { Sparkles, Loader2, RefreshCw, AlertTriangle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/components/ui/use-toast';
import { useAnalyzeTags } from '@/api/tag-suggestions';
import { ApiClientError } from '@/lib/api-client';
import type { TagAnalysisStatus } from '@/types/api';

interface AnalyzeTagsButtonProps {
  locationId: string;
  status: TagAnalysisStatus;
  /** Whether the AI service is available (FR-109) */
  serviceAvailable?: boolean;
  onSuccess?: () => void;
}

export function AnalyzeTagsButton({
  locationId,
  status,
  serviceAvailable = true,
  onSuccess,
}: AnalyzeTagsButtonProps): JSX.Element {
  const [showConfirmDialog, setShowConfirmDialog] = React.useState(false);
  const analyzeMutation = useAnalyzeTags();
  const { toast } = useToast();

  const isAnalyzing = status === 'analyzing';
  const hasBeenAnalyzed = status === 'completed' || status === 'failed' || status === 'timeout';
  const isDisabled = !serviceAvailable || analyzeMutation.isPending;

  const handleAnalyze = (): void => {
    analyzeMutation.mutate(locationId, {
      onSuccess: () => {
        onSuccess?.();
      },
      // Without this a refused or failed start looked like a click that did nothing
      onError: (err) => {
        toast({
          'data-testid': 'analyze-tags-error-toast',
          title: 'Tag analysis did not start',
          // A 4xx message is written for people ("Analysis already in progress"); a 5xx one
          // can be a raw database error, so it is not shown
          description:
            err instanceof ApiClientError && err.statusCode < 500
              ? err.message
              : 'Something went wrong on our side. Try again in a moment.',
          variant: 'destructive',
        });
      },
    });
  };

  // For re-analysis, show confirmation dialog first
  const handleReanalyzeClick = (): void => {
    setShowConfirmDialog(true);
  };

  const handleConfirmReanalyze = (): void => {
    setShowConfirmDialog(false);
    handleAnalyze();
  };

  // Wrapper for tooltip when service unavailable
  const wrapWithTooltip = (button: JSX.Element): JSX.Element => {
    if (!serviceAvailable) {
      return (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>{button}</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>AI service temporarily unavailable</p>
          </TooltipContent>
        </Tooltip>
      );
    }
    return button;
  };

  // When analyzing, show disabled button without additional "Analyzing..." text
  // The center content already shows "Analyzing location..." so we don't duplicate
  if (isAnalyzing) {
    return (
      <Button type="button" variant="outline" size="sm" disabled data-testid="analyze-tags-btn">
        <Sparkles className="h-4 w-4" />
        Analyze Tags
      </Button>
    );
  }

  if (hasBeenAnalyzed) {
    return (
      <>
        {wrapWithTooltip(
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleReanalyzeClick}
            disabled={isDisabled}
            data-testid="analyze-tags-btn"
          >
            {analyzeMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Re-analyze Tags
              </>
            )}
          </Button>
        )}

        {/* Confirmation dialog for re-analysis */}
        <AlertDialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                Re-analyze Tags?
              </AlertDialogTitle>
              <AlertDialogDescription className="text-left">
                This action will trigger a new AI analysis and <strong>override all current AI-suggested tags</strong> across the location and all photos.
                <br /><br />
                Manually added tags will be preserved, but any AI-generated tag suggestions that haven&apos;t been manually confirmed will be replaced with new suggestions.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="reanalyze-cancel-btn">Cancel</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleConfirmReanalyze}
                data-testid="reanalyze-confirm-btn"
              >
                Re-analyze Tags
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  // Initial state or pending - no confirmation needed
  return wrapWithTooltip(
    <Button
      type="button"
      variant="default"
      size="sm"
      onClick={handleAnalyze}
      disabled={isDisabled}
      data-testid="analyze-tags-btn"
    >
      {analyzeMutation.isPending ? (
        <>
          <Loader2 className="h-4 w-4 animate-spin" />
          Starting...
        </>
      ) : (
        <>
          <Sparkles className="h-4 w-4" />
          Analyze Tags
        </>
      )}
    </Button>
  );
}
